import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Download, Eye, FileText, FileSpreadsheet, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SwimLevelBadge from "@/components/SwimLevelBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SwimmerReportModal({ onClose }) {
  const [allCampers, setAllCampers] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [groupFilter, setGroupFilter] = useState("all");

  // Get unique groups from all campers
  const uniqueGroups = useMemo(() => {
    const groups = allCampers
      .map(c => c.bunk)
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    return groups;
  }, [allCampers]);

  // Filter report data based on selected group
  const filteredReportData = useMemo(() => {
    if (!reportData) return null;
    if (groupFilter === "all") return reportData;

    const filteredReport = reportData.report.filter(camper => {
      // The report has name in "Last, First" format, need to find matching camper
      const matchingCamper = allCampers.find(c => 
        `${c.last_name}, ${c.first_name}` === camper.name
      );
      return matchingCamper?.bunk === groupFilter;
    });

    return {
      ...reportData,
      report: filteredReport,
      total: filteredReport.length
    };
  }, [reportData, groupFilter, allCampers]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch both report and campers data
      const [reportResponse, campersResponse] = await Promise.all([
        axios.get(`${API}/swimmer-report?format=json`),
        axios.get(`${API}/campers`)
      ]);
      setReportData(reportResponse.data);
      setAllCampers(campersResponse.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (format) => {
    try {
      setDownloading(true);
      
      // Build URL with group filter if applicable
      let url = `${API}/swimmer-report?format=${format}`;
      if (groupFilter !== "all") {
        url += `&group=${encodeURIComponent(groupFilter)}`;
      }
      
      const response = await axios.get(url, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: format === "pdf" ? "application/pdf" : "text/csv",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      
      // Include group name in filename if filtered
      const filename = groupFilter !== "all" 
        ? `swimmer_report_${groupFilter.replace(/\s+/g, '_')}.${format}`
        : `swimmer_report.${format}`;
      link.download = filename;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(`${format.toUpperCase()} downloaded successfully`);
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.error(`Failed to download ${format.toUpperCase()}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-2xl bg-slate-900 border-slate-800 text-white max-h-[90vh]"
        data-testid="swimmer-report-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 font-['Manrope'] text-xl">
            <FileText className="h-6 w-6 text-sky-400" />
            Swimmer Report
          </DialogTitle>
        </DialogHeader>

        {/* Group Filter */}
        <div className="flex items-center gap-3 py-2">
          <Users className="h-5 w-5 text-slate-400" />
          <Select value={groupFilter} onValueChange={setGroupFilter}>
            <SelectTrigger 
              className="w-full sm:w-[250px] bg-slate-800 border-slate-700 text-white"
              data-testid="report-group-filter"
            >
              <SelectValue placeholder="All Groups" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700 text-white max-h-[300px]">
              <SelectItem value="all" className="hover:bg-slate-700">All Groups ({reportData?.total || 0} campers)</SelectItem>
              {uniqueGroups.map((group) => {
                const count = allCampers.filter(c => c.bunk === group).length;
                return (
                  <SelectItem key={group} value={group} className="hover:bg-slate-700">
                    {group} ({count} campers)
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="view" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-slate-800">
            <TabsTrigger
              value="view"
              className="data-[state=active]:bg-sky-600"
              data-testid="view-tab"
            >
              <Eye className="h-4 w-4 mr-2" />
              View
            </TabsTrigger>
            <TabsTrigger
              value="download"
              className="data-[state=active]:bg-sky-600"
              data-testid="download-tab"
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </TabsTrigger>
          </TabsList>

          <TabsContent value="view" className="mt-4">
            {loading ? (
              <div className="space-y-3" data-testid="report-loading">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-12 bg-slate-800 rounded animate-pulse" />
                ))}
              </div>
            ) : filteredReportData ? (
              <div>
                <div className="flex items-center justify-between mb-4 text-sm text-slate-400">
                  <span>
                    {groupFilter !== "all" 
                      ? `${groupFilter}: ${filteredReportData.total} campers`
                      : `Total: ${filteredReportData.total} campers`
                    }
                  </span>
                  <span>
                    Generated:{" "}
                    {new Date(filteredReportData.generated_at).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}
                  </span>
                </div>

                <ScrollArea className="h-[350px] pr-4">
                  <table className="report-table w-full">
                    <thead>
                      <tr>
                        <th className="rounded-tl-lg">Child's Name</th>
                        <th>Swim Color</th>
                        <th className="rounded-tr-lg">Swim Level</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredReportData.report.map((camper, index) => (
                        <tr key={index} data-testid={`report-row-${index}`}>
                          <td className="text-white font-medium">{camper.name}</td>
                          <td>
                            <SwimLevelBadge color={camper.swim_color} size="sm" />
                          </td>
                          <td className="text-slate-300">{camper.swim_level}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                No data available
              </div>
            )}
          </TabsContent>

          <TabsContent value="download" className="mt-4">
            <div className="space-y-4">
              <p className="text-slate-400 text-sm">
                {groupFilter !== "all" 
                  ? `Download the swimmer report for ${groupFilter} in your preferred format.`
                  : "Download the swimmer report in your preferred format."
                }
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Button
                  onClick={() => handleDownload("pdf")}
                  disabled={downloading}
                  className="h-24 flex-col gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700"
                  variant="outline"
                  data-testid="download-pdf-btn"
                >
                  <FileText className="h-8 w-8 text-red-400" />
                  <span className="font-semibold">Download PDF</span>
                  <span className="text-xs text-slate-400">
                    Professional printable format
                  </span>
                </Button>

                <Button
                  onClick={() => handleDownload("csv")}
                  disabled={downloading}
                  className="h-24 flex-col gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700"
                  variant="outline"
                  data-testid="download-csv-btn"
                >
                  <FileSpreadsheet className="h-8 w-8 text-emerald-400" />
                  <span className="font-semibold">Download CSV</span>
                  <span className="text-xs text-slate-400">
                    For spreadsheet applications
                  </span>
                </Button>
              </div>

              {filteredReportData && (
                <p className="text-center text-sm text-slate-500 mt-4">
                  {groupFilter !== "all" 
                    ? `Report includes ${filteredReportData.total} campers from ${groupFilter}`
                    : `Report includes ${filteredReportData.total} campers`
                  }
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
